/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { type PulseAccordion } from 'glint/pulse';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  accordion: PulseAccordion;
}

export default class StandaloneSalesforceSupportEmail extends Component<Args> {
  @service store: $TSFixMe;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked newSupportEmail?: string;

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig() {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get isLoading() {
    return this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData;
  }

  get salesforceSetupData() {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get supportEmailOptions() {
    let supportEmails = this.salesforcePlatform.salesforceSetupData.supportEmails?.map(
      ({ address, is_freemail }) => ({
        text: address,
        value: address,
        isDisabled: is_freemail,
        tooltipText:
          is_freemail &&
          this.intl.t('standalone.salesforce.setup.from-email-address.freemail-tooltip'),
      }),
    ) as { text: string; value: string; isDisabled?: boolean; tooltipText?: string }[];

    // Handle case where the selected email is not in the list
    if (
      this.selectedSupportEmail !== undefined &&
      !supportEmails.find((email) => email.value === this.selectedSupportEmail)
    ) {
      supportEmails.push({
        text: this.selectedSupportEmail,
        value: this.selectedSupportEmail,
        isDisabled: true,
        tooltipText: this.intl.t(
          'standalone.salesforce.setup.from-email-address.support-email-tooltip',
        ),
      });
    }

    return supportEmails || [];
  }

  get selectedSupportEmail(): string | undefined {
    return this.salesforceConfig.supportEmail || undefined;
  }

  @action selectSupportEmail(supportEmail: string) {
    if (supportEmail === this.selectedSupportEmail) {
      return;
    }

    this.newSupportEmail = supportEmail;
  }

  @action
  async saveSupportEmail() {
    try {
      let response = await this.salesforceConfig.setSupportEmail(
        this.newSupportEmail!.trim().toLowerCase(),
      );
      this.store.push(this.store.normalize('dkim-settings', response.dkim_settings));
      this.store.pushPayload({
        'custom-bounce-settings': response.custom_bounce_settings,
        'custom-email-address': response.support_email_address,
      });
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.from-email-address.success-notification'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t('standalone.salesforce.setup.from-email-address.error-notification'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::SupportEmail': typeof StandaloneSalesforceSupportEmail;
    'standalone/salesforce/setup/support-email': typeof StandaloneSalesforceSupportEmail;
  }
}
